import { DateTime } from "luxon"

import { toCurrency, toDinero, toDineroLossy, toMonetaryAmount, toNumberLossy } from "../../../currency/currencyLib"
import type { DateTimeFactory } from "../../../localization/useDateTime"

import { isWeekDay, normalizeOfferDetails, sortWeekDays } from "./helpers"
import type { CreateProduct, CreateUpdateProductFormValues, Product } from "./productsTypes"

export const toProductFormValues = (
    product: Product,
    dateTimeFactory: DateTimeFactory,
): CreateUpdateProductFormValues => ({
    productGroup: product.productGroup,
    productNumber: product.productNumber,
    name: product.name,
    price: product.price ? toNumberLossy(toDinero(product.price)) : undefined,
    currency: product.price?.currency ?? "DKK",
    shops: product?.shops ?? [],
    description: product.description,
    isActive: product.isActive,
    isAdminOnly: product.isAdminOnly ?? false,
    productPhoto: product.productPhoto,
    ...(product.availabilityDetails
        ? {
              availabilityDetails: {
                  allowPreOrder: product.availabilityDetails.allowPreOrder === false,
                  dateInterval: {
                      from: product.availabilityDetails?.dateInterval?.from
                          ? DateTime.fromISO(product.availabilityDetails?.dateInterval?.from)
                          : null,
                      to: product.availabilityDetails?.dateInterval?.to
                          ? DateTime.fromISO(product.availabilityDetails?.dateInterval?.to)
                          : null,
                  },
                  weekDays: (product.availabilityDetails.weekDays ?? []).filter(isWeekDay),
                  timeInterval: {
                      start: product.availabilityDetails.timeInterval
                          ? dateTimeFactory.fromISO(product.availabilityDetails.timeInterval.start)
                          : null,
                      end: product.availabilityDetails.timeInterval
                          ? dateTimeFactory.fromISO(product.availabilityDetails.timeInterval.end)
                          : null,
                  },
              },
          }
        : {
              availabilityDetails: {
                  weekDays: [],
                  allowPreOrder: false,
              },
          }),
    ...(product.deadline
        ? {
              deadline: {
                  purchases: {
                      daysBefore: Number(product.deadline.purchases.daysBefore),
                      timeOfDay: DateTime.fromISO(product.deadline.purchases.timeOfDay),
                  },
                  // hide cancellation for now
                  // cancellation: {
                  //     daysBefore: Number(product.deadline.cancellation.daysBefore),
                  //     timeOfDay: DateTime.fromISO(product.deadline.cancellation.timeOfDay),
                  // },
              },
          }
        : {}),
    ...(product.offerDetails
        ? {
              offerDetails: normalizeOfferDetails(product.offerDetails),
          }
        : {}),
    ...(product.productDescription
        ? {
              productDescription: product.productDescription,
          }
        : {}),
    // priceLists: product.priceLists
    //     ? product.priceLists.map((pl) => ({
    //           id: pl.id,
    //           name: pl.name,
    //           isActive: true,
    //       }))
    //     : [],
    priceList: product.priceLists?.[0]
        ? { id: product.priceLists[0].id, name: product.priceLists[0].name ?? "", isActive: true }
        : null,
    barcode: product?.barcode,
    variablePrice: product.paymentDetails?.variablePrice ?? false,
    isPriceHidden: product.paymentDetails?.isPriceHidden ?? false,
    isLocked: product.securitySettings?.isLocked ?? false,
    deliveryDetails: product.deliveryDetails
        ? {
              location: {
                  id: product.deliveryDetails.location.id,
              },
          }
        : undefined,
    itemLimits: product.itemLimits || null,
    itemsForSalePerDay: product.stock?.sales?.daily?.itemsForSale,
    // `isActive` is the source of truth once backend support lands; until then fall
    // back to the legacy signal (presence of `inventory`) so existing products with
    // stock still load as ON.
    stock:
        (product.stock?.isActive ?? Boolean(product.stock?.inventory))
            ? { total: product.stock?.inventory?.total }
            : null,
})

export function toCreateProduct(
    values: CreateUpdateProductFormValues,
    dateTimeFactory: DateTimeFactory,
): CreateProduct {
    const {
        productGroup,
        shops,
        price,
        productPhoto,
        productDescription,
        availabilityDetails,
        deadline,
        // priceLists,
        priceList,
        deliveryDetails,
        itemLimits,
        productGroupCode,
        variablePrice,
        isPriceHidden,
        isLocked,
        stock,
        itemsForSalePerDay,
        ...productValues
    } = values

    const parsedItemsForSale = Number(itemsForSalePerDay)
    const hasItemsForSale =
        itemsForSalePerDay != null &&
        String(itemsForSalePerDay).trim() !== "" &&
        !Number.isNaN(parsedItemsForSale) &&
        parsedItemsForSale > 0

    const isStockManagementEnabled = stock != null

    const productGroupCodeBody = productGroupCode
        ? {
              productGroupCode: {
                  id: productGroupCode.id,
              },
          }
        : {}

    const modifyData = {
        ...productValues,
        ...productGroupCodeBody,
        productGroup: {
            id: productGroup?.id!,
        },
        shops: shops.map((shop) => ({
            id: shop.id,
        })),
        ...(productDescription
            ? {
                  productDescription: {
                      ...productDescription,
                      allergens: (productDescription.allergens ?? []).map((allergen) => ({
                          id: allergen.id,
                      })),
                  },
              }
            : {}),
        availabilityDetails: {
            allowPreOrder: !availabilityDetails?.allowPreOrder,
            ...(availabilityDetails?.weekDays?.length ? { weekDays: sortWeekDays(availabilityDetails.weekDays) } : {}),
            ...(availabilityDetails?.timeInterval?.start && availabilityDetails.timeInterval.end
                ? {
                      timeInterval: {
                          start: dateTimeFactory.toHourMinute(availabilityDetails.timeInterval.start),
                          end: dateTimeFactory.toHourMinute(availabilityDetails.timeInterval.end),
                      },
                  }
                : {}),
            ...(availabilityDetails?.dateInterval?.from || availabilityDetails?.dateInterval?.to
                ? {
                      dateInterval: {
                          ...(availabilityDetails?.dateInterval?.from
                              ? { from: availabilityDetails?.dateInterval?.from.toISODate() }
                              : {}),
                          ...(availabilityDetails?.dateInterval?.to
                              ? { to: availabilityDetails?.dateInterval?.to.toISODate() }
                              : {}),
                      },
                  }
                : {}),
        },
        ...(deadline
            ? {
                  deadline: {
                      purchases: {
                          ...deadline.purchases,
                          timeOfDay: deadline.purchases.timeOfDay.toFormat("HH:mm"),
                      },
                      // hide cancellation for now
                      // cancellation: {
                      //     ...deadline.cancellation,
                      //     timeOfDay: deadline.cancellation.timeOfDay.toFormat("HH:mm"),
                      // },
                  },
              }
            : {}),
        // ...(priceLists && priceLists.length > 0
        //     ? {
        //           priceLists: priceLists.map((priceList) => ({
        //               id: priceList.id,
        //           })),
        //       }
        //     : {}),
        ...(priceList
            ? {
                  priceLists: [{ id: priceList.id }],
              }
            : {}),
        ...(deliveryDetails
            ? {
                  deliveryDetails: { location: { id: deliveryDetails?.location.id } },
              }
            : {}),
        paymentDetails: {
            variablePrice,
            isPriceHidden,
        },
        securitySettings: {
            isLocked,
        },
        ...(!variablePrice && price != null
            ? { price: toMonetaryAmount(toDineroLossy(price, toCurrency(values.currency))) }
            : {}),
        ...(itemLimits && (itemLimits.maxDailyItemsPerPerson || itemLimits.minItemsPerOrder)
            ? {
                  itemLimits: itemLimits,
              }
            : {}),
        ...(isStockManagementEnabled || hasItemsForSale
            ? {
                  stock: {
                      isActive: isStockManagementEnabled,
                      ...(stock?.inventory ? { inventory: stock.inventory } : {}),
                      ...(hasItemsForSale ? { sales: { daily: { itemsForSale: parsedItemsForSale } } } : {}),
                  },
              }
            : {}),
    }

    if (!productPhoto?.uid && productPhoto?.url) {
        return {
            ...modifyData,
            productPhoto: {
                url: productPhoto?.url,
            },
        }
    }

    return {
        ...modifyData,
        ...(productPhoto?.uid !== undefined
            ? {
                  productPhoto: {
                      uid: productPhoto?.uid,
                  },
              }
            : {}),
    }
}
